import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { sectionPath } from "@/components/job/Lifecycle";
import { AskTeammateDialog } from "@/components/assessment/AskTeammates";
import { approveTrial } from "@/components/assessment/approve";
import { ago } from "@/lib/utils";
import { ArrowRight, Check, Loader2 } from "lucide-react";

function copyFor(t, uid, approval, live) {
  const signoffs = t.signoffs || [];
  const decided = new Set(signoffs.map((s) => s.reviewer_id));
  const asked = (t.signoff_requests || []).find((r) => r.reviewer_id === uid && !decided.has(uid));
  const waiting = (t.signoff_requests || []).filter((r) => !decided.has(r.reviewer_id)).map((r) => r.name || r.email);
  const fit = "Approve it if the questions are fair, accurate and right for the role.";
  if (approval) return [live ? "Approved and live" : "Approved", `By ${approval.reviewer}, ${ago(approval.at)}.${live ? "" : " Publish it when you're ready."}`];
  if (asked) return [`${asked.requested_by || "A teammate"} asked for your review`, fit];
  if (waiting.length) return ["Ready to publish this assessment?", `Waiting on ${waiting.join(", ")}. You can also approve it yourself.`];
  return ["Ready to publish this assessment?", live ? fit : `${fit} Approving publishes the role on your careers page.`];
}

function History({ signoffs }) {
  return signoffs.map((s, i) => (
    <span key={i} data-testid={`signoff-${i}`}>
      {s.reviewer} {s.decision === "approved" ? "approved" : "requested changes"} · {ago(s.at)}{s.note ? `: “${s.note}”` : ""}
    </span>
  ));
}

function ChangesForm({ trialId, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!note.trim()) { toast.error("Add a note so the team knows what to change"); return; }
    setBusy(true);
    try { await api.post(`/test/trial/${trialId}/signoff`, { decision: "needs_changes", note }); toast.success("Changes requested"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2" data-testid="signoff-changes-form">
      <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={2000} autoFocus placeholder="What should change?" className="flex-1" data-testid="signoff-note" />
      <Button variant="outline" onClick={send} disabled={busy} data-testid="signoff-changes-send">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Send</Button>
    </div>
  );
}

// The trial report's decision bar: approve (and publish) in one click; a teammate's review is optional.
export function SignoffPanel({ t, job, onChanged }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [asking, setAsking] = useState(false);
  const [changes, setChanges] = useState(false);
  const [busy, setBusy] = useState(false);
  const signoffs = t.signoffs || [];
  const approval = [...signoffs].reverse().find((s) => s.decision === "approved");
  const live = job?.status === "published";
  const [heading, sub] = copyFor(t, user?.id, approval, live);
  const approve = async () => {
    setBusy(true);
    const res = await approveTrial(t.id, { publish: !live, title: t.job_title });
    setBusy(false);
    if (res) onChanged();
  };
  return (
    <section className="hs-card px-5 py-4 space-y-3" data-testid="report-signoff" data-state={approval ? "approved" : "pending"}>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="min-w-0 flex-1 space-y-0.5">
          <h2 className="text-[15px] font-semibold flex items-center gap-2" data-testid="signoff-heading">{approval && <Check className="h-4 w-4 text-primary" />}{heading}</h2>
          <p className="text-sm text-muted-foreground" data-testid="signoff-sub">{sub}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {approval ? (live
            ? <Button variant="outline" onClick={() => nav(sectionPath(t.job_id, "screening"))} data-testid="report-open-role">Open the role</Button>
            : <Button onClick={() => nav(sectionPath(t.job_id, "publish"))} className="gap-1.5" data-testid="report-continue">Continue to publish<ArrowRight className="h-4 w-4" /></Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setChanges((c) => !c)} aria-expanded={changes} data-testid="signoff-changes">Request changes</Button>
              <Button onClick={approve} disabled={busy || !job} className="gap-1.5" data-testid="signoff-approve">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}{live ? "Approve" : "Approve and publish"}
              </Button>
            </>
          )}
        </div>
      </div>
      {changes && !approval && <ChangesForm trialId={t.id} onDone={() => { setChanges(false); onChanged(); }} />}
      {(!approval || signoffs.length > 0) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border pt-3 text-xs text-muted-foreground" data-testid="signoff-footer">
          {!approval && <button onClick={() => setAsking(true)} className="text-primary hover:underline" data-testid="signoff-ask-link">Ask a teammate to review</button>}
          <History signoffs={signoffs} />
        </div>
      )}
      <AskTeammateDialog trialId={t.id} open={asking} onOpenChange={setAsking} onSent={onChanged} />
    </section>
  );
}
